import { getRequiredFields } from "components/form/utils";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useMemo, useRef } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { meetingSchema } from "./schema";
import { fetchMeetingDataForForm } from "./read";
import { useCreateMeeting } from "../create/mutation";
import { useUpdateMeeting } from "../update/mutation";

export const useMeetingForm = ({ defaultMeetingId }) => {
  const createMeetingMutation = useCreateMeeting();
  const updateMeetingMutation = useUpdateMeeting();
  const navigate = useNavigate();
  const originalParticipantsRef = useRef([]);

  const form = useForm({
    resolver: zodResolver(meetingSchema),
    defaultValues: {
      id: defaultMeetingId || "",
      title: "",
      starts_at: null,
      ends_at: null,
      participants: [],
    },
  });

  const requiredFields = useMemo(
    () => getRequiredFields(meetingSchema),
    [meetingSchema]
  );

  const handleCreate = async (context) => {
    const values = context.values;

    // Convert datetime values from browser timezone to UTC for API
    const valuesForAPI = {
      ...values,
      starts_at: values.starts_at ? new Date(values.starts_at).toISOString() : null,
      ends_at: values.ends_at ? new Date(values.ends_at).toISOString() : null,
    };

    const response = await createMeetingMutation.mutateAsync({ values: valuesForAPI });
    navigate(`/apps/meet`);
  };

  async function handleRead(id) {
    const response = await fetchMeetingDataForForm({ id });

    // Transform participants from API format to form format
    let participants = [];
    if (response.participants && response.participants.length > 0) {
      participants = response.participants.map((p, idx) => ({
        id: p.participant?.id || null,
        email: p.participant?.email || "",
        participantId: p.id, // Store the relationship ID for updates
        _isNew: false,
      }));
    }

    // Store original participants for delete tracking
    originalParticipantsRef.current = JSON.parse(JSON.stringify(participants));

    const values = {
      ...response,
      participants,
      starts_at: response.starts_at,
      ends_at: response.ends_at,
    };
    return values;
  }

  async function handleUpdate(id, context) {
    const currentFormValues = form.getValues();
    const originalParticipants = originalParticipantsRef.current;

    // Find deleted participants
    const currentParticipantIds = new Set(
      currentFormValues.participants.map(p => p.participantId).filter(Boolean)
    );

    const deletedParticipants = originalParticipants
      .filter(p => !currentParticipantIds.has(p.participantId))
      .map(p => p.participantId);

    // Convert datetime values from browser timezone to UTC for API
    const valuesForAPI = {
      ...currentFormValues,
      deletedParticipantIds: deletedParticipants,
      starts_at: currentFormValues.starts_at ? new Date(currentFormValues.starts_at).toISOString() : null,
      ends_at: currentFormValues.ends_at ? new Date(currentFormValues.ends_at).toISOString() : null,
    };

    await updateMeetingMutation.mutateAsync({
      meetingId: id,
      values: valuesForAPI,
    });
    navigate(`/apps/meet`);
  }

  return {
    form,
    requiredFields,
    handleCreate,
    handleRead,
    handleUpdate,
  };
};
